import * as m from '@/paraglide/messages';
import { SettingsRowsSkeleton } from '@/components/web-fe/design-v2/skeletons';
import {
  SettingsCard,
  SettingsPaneHeader,
} from '@/components/web-fe/design-v2/settings-shell';
import { DropboxIntegration } from '@/components/web-fe/design-v2/dropbox-integration';
import { GoogleDriveIntegration } from '@/components/web-fe/design-v2/google-drive-integration';
import {
  IntegrationButton,
  IntegrationRow,
} from '@/components/web-fe/design-v2/integration-row';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  ArrowUpRight,
  Link,
  OpenBook,
  RefreshDouble,
  Xmark,
} from 'iconoir-react';
import { useEffect } from 'react';
import { Match, Schema } from 'effect';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  disconnectZoteroEffect,
  zoteroKeys,
  zoteroStatusEffect,
} from '@listening/frontend/query/zotero';
import { eq } from '@/runtimes/effect-query';
import { optionalCatching } from '@/lib/search-params';
import { authClient } from '@/lib/auth-client';
import { clientEnv } from '@/env';
import {
  IntegrationConnectionFailureReasonSchema,
  type IntegrationConnectionFailureReason,
} from '@listening/api-contract/integration-providers';

function connectionFailureMessage(
  integration: 'dropbox' | 'drive',
  reason: IntegrationConnectionFailureReason | undefined,
) {
  const provider =
    integration === 'dropbox' ? m.dropbox_title() : m.google_drive_title();
  return Match.value(reason).pipe(
    Match.when('expired', () => m.integration_connect_expired({ provider })),
    Match.when('not_authenticated', () =>
      m.integration_connect_not_authenticated({ provider }),
    ),
    Match.when('permission_denied', () =>
      m.integration_connect_permission_denied({ provider }),
    ),
    Match.when('unavailable', () =>
      m.integration_connect_unavailable({ provider }),
    ),
    Match.orElse(() =>
      integration === 'dropbox' ? m.dropbox_error() : m.google_drive_error(),
    ),
  );
}

// TanStack Router's default search parser runs JSON.parse on each value, so
// `?connected=true` arrives as a boolean. Typing it as such keeps the redirect
// URL unchanged while letting the route schema validate cleanly.
const searchSchema = Schema.toStandardSchemaV1(
  Schema.Struct({
    dropbox: optionalCatching(
      Schema.Literals(['connected', 'canceled', 'failed']),
    ),
    drive: optionalCatching(
      Schema.Literals(['connected', 'canceled', 'failed']),
    ),
    reason: optionalCatching(IntegrationConnectionFailureReasonSchema),
    connected: optionalCatching(Schema.Boolean),
    error: optionalCatching(Schema.String),
  }),
);

export const Route = createFileRoute(
  '/app/_authenticated/_dashboard/settings/integrations',
)({
  component: IntegrationsPage,
  pendingComponent: SettingsRowsSkeleton,
  pendingMs: 0,
  validateSearch: searchSchema,
  beforeLoad: async ({ search }) => {
    if (
      search.connected === true ||
      search.dropbox === 'connected' ||
      search.drive === 'connected'
    ) {
      await authClient.getSession({ query: { disableCookieCache: true } });
    }
  },
  context: () => ({
    title: m.settings_integrations_title(),
    icon: Link,
  }),
});

function IntegrationsPage() {
  const { connected, error, dropbox, drive, reason } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const statusQuery = useQuery(
    eq.queryOptions({
      queryKey: zoteroKeys.status,
      queryFn: () => zoteroStatusEffect,
      enabled: clientEnv.zoteroUiEnabled,
    }),
  );

  const disconnectMutation = useMutation(
    eq.mutationOptions({
      mutationFn: () => disconnectZoteroEffect,
      onSuccess: async () => {
        void queryClient.invalidateQueries({ queryKey: zoteroKeys.status });
        // Refresh session so the home-screen Zotero upload card (gated on
        // `user.enabledIntegrations`) hides immediately.
        await authClient.getSession({ query: { disableCookieCache: true } });
        toast.success(m.settings_integrations_zotero_disconnect_success());
      },
      onError: () => {
        toast.error(m.settings_integrations_zotero_disconnect_error());
      },
    }),
  );

  useEffect(() => {
    if (!connected && !error && !dropbox && !drive) return;
    if (connected === true) {
      toast.success(m.settings_integrations_zotero_connect_success());
    }
    if (error) {
      toast.error(
        m.settings_integrations_zotero_connect_error({ reason: error }),
      );
    }
    if (dropbox === 'connected') toast.success(m.dropbox_connected());
    if (dropbox === 'failed')
      toast.error(connectionFailureMessage('dropbox', reason));
    if (drive === 'connected') toast.success(m.google_drive_connected());
    if (drive === 'failed')
      toast.error(connectionFailureMessage('drive', reason));
    if (dropbox === 'canceled' || drive === 'canceled') {
      toast(m.integration_connection_canceled());
    }
    void navigate({
      to: '/app/settings/integrations',
      search: {},
      replace: true,
    });
  }, [connected, error, dropbox, drive, reason, navigate]);

  const status = statusQuery.data;

  return (
    <>
      <SettingsPaneHeader title={m.settings_integrations_title()} />
      <SettingsCard>
        <DropboxIntegration />
        <GoogleDriveIntegration />
        {clientEnv.zoteroUiEnabled && (
          <IntegrationRow
            icon={<OpenBook />}
            title={m.settings_integrations_zotero_title()}
            description={m.settings_integrations_zotero_description()}
            action={
              statusQuery.isPending ? (
                <RefreshDouble className='size-5 animate-spin text-[var(--v2-text-muted)]' />
              ) : (
                !status?.connected && (
                  <IntegrationButton
                    aria-label={m.settings_integrations_zotero_connect()}
                    onClick={() => {
                      window.location.href =
                        '/api/v2/integrations/zotero/connect';
                    }}
                  >
                    {m.common_connect()}
                    <ArrowUpRight />
                  </IntegrationButton>
                )
              )
            }
            details={
              status?.connected ? (
                <>
                  <span>
                    {status.display_name
                      ? m.settings_integrations_zotero_connected_as({
                          displayName: status.display_name,
                        })
                      : m.common_connected()}
                  </span>
                  <div className='flex flex-wrap gap-2'>
                    <IntegrationButton
                      disabled={disconnectMutation.isPending}
                      onClick={() => disconnectMutation.mutate(undefined)}
                    >
                      {disconnectMutation.isPending ? (
                        <RefreshDouble className='size-4 animate-spin' />
                      ) : (
                        <>
                          {m.common_disconnect()}
                          <Xmark />
                        </>
                      )}
                    </IntegrationButton>
                  </div>
                </>
              ) : undefined
            }
          />
        )}
      </SettingsCard>
    </>
  );
}
